import { HttpEventType } from '@angular/common/http';
import {
  Component,
  ElementRef,
  ErrorHandler,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { catchError, of, Subscription, take } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { FileDTO } from '../../../dtos/file.dto';
import { ProformViewStates } from '../../../dtos/proforms.dto';
import { FileType } from '../../../enums/file-type.enum';
import { fileUploadItemResponseToFileDTO } from '../../../helpers/translators/files.translators';
import { ProformsSubmissionService } from '../../../services/proforms/proforms-submission.service';
import {
  ProformFieldTypes,
  ProFormsQuestionAPIResponse,
} from '../../../types/responses/proforms.responses';
import { UploadStyles } from '../../file-management/file-upload/file-upload.component';

interface UploadingItemDTO {
  id: string;
  name: string;
  progress: number;
}

@Component({
  selector: 'app-proforms-question-answer-attachment',
  standalone: false,

  templateUrl: './proforms-question-answer-attachment.component.html',
  styleUrl: './proforms-question-answer-attachment.component.scss',
})
export class ProformsQuestionAnswerAttachmentComponent
  implements OnInit, OnDestroy
{
  @Input() question: ProFormsQuestionAPIResponse;

  @Input() viewState: ProformViewStates = ProformViewStates.PREVIEW;

  @ViewChild('fileUpload') public fileUpload: ElementRef<HTMLInputElement>;

  subs: Subscription[] = [];

  FieldTypes = ProformFieldTypes;

  viewStates = ProformViewStates;

  uploadStyle = UploadStyles.SIMPLE;

  attachments: FileDTO[] = [];

  allowedFileTypes = FileType.DEFAULT;

  disabled = false;

  isUploading = false;

  dropzoneActive = false;

  uploadingItems: { [id: string]: UploadingItemDTO } = {};

  errorMsg = '';

  constructor(
    private proformsSubmissionService: ProformsSubmissionService,
    private errorHandlerService: ErrorHandler
  ) {}

  ngOnInit(): void {
    if (this.question) {
      if (
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED
      ) {
        this.proformsSubmissionService.currentSubmission.subscribe(
          (submission) => {
            this.attachments = [];

            if (submission?.answers) {
              submission.answers
                .filter(
                  (a) =>
                    a.proform_form_question_id === this.question.id &&
                    a.answerAttachment
                )
                .forEach((answer) => {
                  if (!answer.answerAttachment) {
                    return;
                  }
                  const newFileDTO = fileUploadItemResponseToFileDTO(
                    answer.answerAttachment
                  );
                  this.updateAttachmentsList(newFileDTO);
                });
            }
          }
        );
      } else if (this.viewState === this.viewStates.EDIT) {
        // When editing an existing submission, load in existing answers
        this.proformsSubmissionService.currentSubmission.subscribe(
          (submission) => {
            this.attachments = [];

            if (submission?.answers && submission.answers.length > 0) {
              submission.answers
                .filter(
                  (a) =>
                    a.proform_form_question_id === this.question.id &&
                    a.answerAttachment
                )
                .forEach((answer) => {
                  if (!answer.answerAttachment) {
                    return;
                  }
                  const newFileDTO = fileUploadItemResponseToFileDTO(
                    answer.answerAttachment
                  );
                  this.updateAttachmentsList(newFileDTO);
                });
            }
          }
        );
        // Load in form values from local storage if there are any
        this.proformsSubmissionService.newSubmission
          .pipe(take(1))
          .subscribe((submission) => {
            if (submission) {
              if (submission.answers) {
                submission.answers
                  .filter(
                    (a) =>
                      a.proform_form_question_id === this.question.id &&
                      a.answerAttachment
                  )
                  .forEach((answer) => {
                    if (!answer.answerAttachment) {
                      return;
                    }
                    const newFileDTO = fileUploadItemResponseToFileDTO(
                      answer.answerAttachment
                    );
                    this.updateAttachmentsList(newFileDTO);
                  });
              }
            }
          });
      }
    }
  }

  get isInputInteractive(): boolean {
    return this.viewState === this.viewStates.EDIT;
  }

  updateAttachmentsList(file: FileDTO): void {
    if (this.question.field_type === this.FieldTypes.ATTACHMENT) {
      this.attachments.push(file);
    }
  }

  onUploadButtonClick() {
    this.fileUpload.nativeElement.click();
  }

  toggleDropZone(status: boolean) {
    this.dropzoneActive = status;
  }

  handleDrop(event: DragEvent) {
    this.toggleDropZone(false);
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      Array.from(event.dataTransfer.files).forEach((file: File) => {
        this.uploadFile(file);
      });
    }
  }

  setUploadingStatus(status: boolean) {
    this.isUploading = status;
    if (status) {
      this.fileUpload.nativeElement.setAttribute('disabled', 'disabled');
    } else {
      this.fileUpload.nativeElement.removeAttribute('disabled');
    }
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      Array.from(input.files).forEach((file: File) => {
        this.uploadFile(file);
      });
      this.fileUpload.nativeElement.value = '';
    }
  }

  uploadFile(file: File) {
    // Remove spaces from file name to prevent error on file download, and retain file type
    const modifiedFile = new File([file], file.name.split(' ').join(''), {
      type: file.type,
    });

    this.setUploadingStatus(true);
    const id = uuid();
    const uploadingItem: UploadingItemDTO = {
      id,
      name: modifiedFile.name,
      progress: 0,
    };

    this.uploadingItems[id] = uploadingItem;
    this.subs.push(
      this.proformsSubmissionService
        .uploadAttachmentImage(modifiedFile, this.question.id)
        .pipe(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          catchError((err: any) => {
            this.errorMsg = `${err}`;
            this.errorHandlerService.handleError(this.errorMsg);
            this.dropzoneActive = true;
            this.setUploadingStatus(false);
            this.uploadingItems = {};
            return of({ type: 'error', message: err });
          })
        )
        .subscribe((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            uploadingItem.progress = Math.round(
              100 * (event.loaded / event.total)
            );
          }
          if (event.type === HttpEventType.Response) {
            const newFileDTO = fileUploadItemResponseToFileDTO(event.body.item);

            // UPDATE LOCAL FILE LIST
            this.updateAttachmentsList(newFileDTO);

            // SYNC ATTACHMENT ANSWER WITH SERVICE PAYLOAD
            // (one answer per uploaded file, preserving answerAttachment)
            this.proformsSubmissionService.addAnswerToPayload(
              {
                proform_form_question_id: this.question.id,
                text_answer: newFileDTO.location,
                answerAttachment: event.body.item,
              },
              this.FieldTypes.ATTACHMENT
            );

            // RESET UPLOAD COMPONENT
            delete this.uploadingItems[id];

            this.setUploadingStatus(false);

            if (this.dropzoneActive) {
              this.dropzoneActive = false;
            }
          }
        })
    );
  }

  deleteAttachment(file: FileDTO) {
    this.attachments = this.attachments.filter(
      (attachment) => attachment.id !== file.id
    );

    this.proformsSubmissionService.removeAttachmentAnswer(
      this.question.id,
      file.location
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
    this.subs = [];
  }
}
